import {
  ActionConfirmationModal,
  AppModal,
  Button,
  LoadingSpinner,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveObjectUpdatesInfiniteQueryOptions } from "@/lib/data/react-query/updates/object-updates";
import type { UpdateAnchorType } from "@/lib/data/schemas/updates/ObjectUpdateSchema";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
  PAGE_PERMISSION_KEYS,
} from "@/lib/data/static/permissionOptions";
import { SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION } from "@/lib/data/static/slideoverStatics";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  ObjectUpdateComposer,
  type UpdateComposerState,
} from "./ObjectUpdateComposer";

interface CreateObjectUpdateModalProps {
  anchorObjectType: UpdateAnchorType;
  anchorObjectId: string;
  entityLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateObjectUpdateModal({
  anchorObjectType,
  anchorObjectId,
  entityLabel,
  open,
  onOpenChange,
}: CreateObjectUpdateModalProps) {
  const { hasActionPermission, hasPagePermission } = useUser();
  const [composerState, setComposerState] = useState<UpdateComposerState>({
    isDirty: false,
    isSubmitting: false,
  });
  const blocker = useBlocker({
    condition: composerState.isDirty || composerState.isSubmitting,
  });
  useEffect(() => {
    if (
      blocker.status === "blocked" &&
      !composerState.isDirty &&
      !composerState.isSubmitting
    )
      blocker.proceed();
  }, [blocker, composerState]);
  const hasAccess =
    hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailUpdates) &&
    (anchorObjectType !== "matter_expense" ||
      hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailCosts)) &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.objectUpdate,
      ACTION_PERMISSION_VALUES.create,
    );
  const { data, isPending, isError, isFetching, refetch } = useInfiniteQuery({
    ...retrieveObjectUpdatesInfiniteQueryOptions({
      anchorObjectType,
      anchorObjectId,
    }),
    enabled: open && hasAccess,
  });
  const canCreate = hasAccess && Boolean(data?.pages[0]?.data.canCreate);

  return (
    <>
      <AppModal
        open={open}
        onOpenChange={(nextOpen) => {
          if (!composerState.isSubmitting) onOpenChange(nextOpen);
        }}
        title={`Create update for ${entityLabel}`}
        size="2xl"
        isDirty={composerState.isDirty && !composerState.isSubmitting}
        hideCloseButton={composerState.isSubmitting}
      >
        {!hasAccess ? (
          <Text>You do not have access to create updates.</Text>
        ) : isPending ? (
          <div
            className="flex min-h-48 items-center justify-center"
            role="status"
          >
            <LoadingSpinner className="h-6 w-6" />
            <span className="sr-only">Loading update permissions</span>
          </div>
        ) : canCreate ? (
          <ObjectUpdateComposer
            anchorObjectType={anchorObjectType}
            anchorObjectId={anchorObjectId}
            autoFocus
            onStateChange={setComposerState}
            onSaved={() => {
              onOpenChange(false);
            }}
            onCancel={() => {
              onOpenChange(false);
            }}
          />
        ) : isError ? (
          <div className="flex flex-col items-center gap-3 py-4" role="alert">
            <Text className="text-destructive">
              Could not load update permissions.
            </Text>
            <Button
              variant="outline"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              Try again
            </Button>
          </div>
        ) : (
          <Text>You do not have access to create updates.</Text>
        )}
      </AppModal>
      <ActionConfirmationModal
        open={blocker.status === "blocked" && !composerState.isSubmitting}
        setOpen={(nextOpen) => {
          if (!nextOpen && blocker.status === "blocked") blocker.reset();
        }}
        title="Unsaved changes"
        description={SLIDEOVER_CONFIRM_CLOSE_DESCRIPTION}
        buttonText="Confirm"
        actionVariant="default"
        afterActionFn={() => {
          if (blocker.status === "blocked") blocker.proceed();
        }}
      />
    </>
  );
}
